import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";
import type { PermissionGroup } from "@/lib/api/roles";

interface PermissionMatrixProps {
  groups: PermissionGroup[];
  selectedPermissions: string[];
  onPermissionsChange: (permissions: string[]) => void;
  disabled?: boolean;
}

export const PermissionMatrix = ({
  groups,
  selectedPermissions,
  onPermissionsChange,
  disabled = false,
}: PermissionMatrixProps) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const togglePermission = (permissionKey: string) => {
    if (disabled) return;

    const newPermissions = selectedPermissions.includes(permissionKey)
      ? selectedPermissions.filter((p) => p !== permissionKey)
      : [...selectedPermissions, permissionKey];

    onPermissionsChange(newPermissions);
  };

  const toggleGroup = (groupLabel: string) => {
    const newSet = new Set(expandedGroups);
    if (newSet.has(groupLabel)) {
      newSet.delete(groupLabel);
    } else {
      newSet.add(groupLabel);
    }
    setExpandedGroups(newSet);
  };

  const toggleAllInGroup = (group: PermissionGroup, checked: boolean) => {
    if (disabled) return;

    const groupPermissions = (group.permissions || []).map((p) => p.key).filter(Boolean);

    if (checked) {
      const newPermissions = [...new Set([...selectedPermissions, ...groupPermissions])];
      onPermissionsChange(newPermissions);
    } else {
      const newPermissions = selectedPermissions.filter((p) => !groupPermissions.includes(p));
      onPermissionsChange(newPermissions);
    }
  };

  const toggleAllPermissions = (checked: boolean) => {
    if (disabled) return;

    if (checked) {
      const allPermissions = groups
        .filter((g) => g.permissions && Array.isArray(g.permissions))
        .flatMap((g) => g.permissions.map((p) => p.key).filter(Boolean));
      onPermissionsChange(allPermissions);
      setExpandedGroups(new Set(groups.map((g) => g.label)));
    } else {
      onPermissionsChange([]);
    }
  };

  const getAllPermissionKeys = (): string[] => {
    if (!groups || groups.length === 0) return [];
    return groups.flatMap((g) => {
      if (!g.permissions || !Array.isArray(g.permissions)) return [];
      return g.permissions.map((p) => p.key).filter(Boolean);
    });
  };

  const allSelected =
    selectedPermissions.length === getAllPermissionKeys().length &&
    getAllPermissionKeys().length > 0;
  const someSelected = selectedPermissions.length > 0 && !allSelected;

  return (
    <div className="space-y-2">
      {/* Select All Header */}
      <div className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
        <div className="flex items-center gap-3">
          <Checkbox
            id="select-all-permissions"
            checked={allSelected}
            onCheckedChange={(checked) => toggleAllPermissions(checked === true)}
            disabled={disabled}
            className={cn(someSelected && "data-[state=unchecked]:bg-primary/30")}
          />
          <Label htmlFor="select-all-permissions" className="font-medium cursor-pointer">
            Select all permissions
          </Label>
        </div>
        <span className="text-sm text-gray-500">
          {selectedPermissions.length} of {getAllPermissionKeys().length} selected
        </span>
      </div>

      {/* Permission Groups */}
      {groups.map((group) => {
        const isExpanded = expandedGroups.has(group.label);
        const groupPermissions = group.permissions.map((p) => p.key);
        const selectedCount = groupPermissions.filter((key) =>
          selectedPermissions.includes(key)
        ).length;
        const allSelectedInGroup = selectedCount === groupPermissions.length;
        const someSelectedInGroup = selectedCount > 0 && selectedCount < groupPermissions.length;

        return (
          <div
            key={group.label}
            className={cn(
              "border rounded-lg transition-colors",
              isExpanded ? "border-gray-200 bg-gray-50/50" : "border-gray-100"
            )}
          >
            <div
              className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
              onClick={() => toggleGroup(group.label)}
            >
              <div className="flex items-center gap-3">
                <Checkbox
                  checked={allSelectedInGroup}
                  onCheckedChange={(checked) => toggleAllInGroup(group, checked === true)}
                  onClick={(e) => e.stopPropagation()}
                  disabled={disabled}
                  className={cn(someSelectedInGroup && "data-[state=unchecked]:bg-primary/30")}
                />
                <div>
                  <span className="font-medium text-gray-900">{group.label}</span>
                  <span className="ml-2 text-xs text-gray-500">
                    {selectedCount} of {groupPermissions.length} selected
                  </span>
                </div>
              </div>
              {isExpanded ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </div>

            {isExpanded && (
              <div className="px-4 pt-0 pb-4 space-y-3 ml-7">
                {!group.permissions || group.permissions.length === 0 ? (
                  <div className="text-sm text-gray-500">No permissions in this group</div>
                ) : (
                  group.permissions.map((permission) => (
                    <div
                      key={permission.key}
                      className="flex items-start gap-3 p-2 rounded hover:bg-white"
                    >
                      <Checkbox
                        id={permission.key}
                        checked={selectedPermissions.includes(permission.key)}
                        onCheckedChange={() => togglePermission(permission.key)}
                        disabled={disabled}
                      />
                      <div className="flex-1">
                        <Label
                          htmlFor={permission.key}
                          className="font-normal text-gray-700 cursor-pointer"
                        >
                          {permission.label}
                        </Label>
                        {permission.description && (
                          <p className="text-xs text-gray-500 mt-0.5">{permission.description}</p>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        );
      })}

      {selectedPermissions.length === 0 && (
        <div className="flex items-start gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="h-5 w-5 text-gray-400 shrink-0 mt-0.5">ℹ️</div>
          <div>
            <p className="text-sm font-medium text-gray-700">No permissions selected</p>
            <p className="text-xs text-gray-500">
              Select permissions to grant access to specific features and actions.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
